import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Phone, Mail, MapPin, Linkedin, Twitter, Facebook, Youtube,
  ArrowRight, Award, Users, Building2, Globe
} from 'lucide-react'

const footerLinks = {
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Chairman Message', href: '/about#leadership' },
    { name: 'Group Companies', href: '/group-companies' },
    { name: 'Careers', href: '/careers' },
    { name: 'Media Center', href: '/media-center' },
    { name: 'Contact Us', href: '/contact' },
  ],
  services: [
    { name: 'Furniture Solutions', href: '/services/furniture' },
    { name: 'Smart Education', href: '/services/smart-education' },
    { name: 'Digital Transformation', href: '/services/digital' },
    { name: 'Healthcare Solutions', href: '/services/healthcare' },
    { name: 'Infrastructure', href: '/services/infrastructure' },
    { name: 'Security Solutions', href: '/services/security' },
  ],
  industries: [
    { name: 'Government', href: '/industries#government' },
    { name: 'Education', href: '/industries#education' },
    { name: 'Healthcare', href: '/industries#healthcare' },
    { name: 'Corporate', href: '/industries#corporate' },
    { name: 'Smart Cities', href: '/industries#smart-cities' },
  ],
  partnerships: [
    { name: 'Become a Partner', href: '/franchise' },
    { name: 'Franchise Program', href: '/franchise#franchise' },
    { name: 'Distribution Network', href: '/franchise#distribution' },
    { name: 'Vendor Registration', href: '/franchise#vendor' },
  ],
}

const groupCompanies = [
  'GSM Corp',
  'GPMM Enterprises',
  'Ved Enterprises',
  'Adhya Enterprises',
  'ADMS Technoways',
  'Enteka Systems Pvt Ltd',
  'Mandate IT Solutions Pvt Ltd',
  'Aarambh Enterprises',
  'VSM Enterprises',
  'Eonixa Dynamics Pvt Ltd',
  'AHAD Solutions Pvt Ltd',
]

const partners = [
  'Hindustan Unilever',
  'Godrej Interio',
  'Titan Company',
  'Mia by Tanishq',
  'Blackberrys',
  'Helios',
  'Frozen Bottle',
  'Momo Panda',
]

const stats = [
  { icon: Building2, value: '₹150+', label: 'Crore Business' },
  { icon: Users, value: '11', label: 'Group Companies' },
  { icon: Globe, value: '50+', label: 'Districts Covered' },
  { icon: Award, value: '7+', label: 'Years Experience' },
]

export default function Footer() {
  return (
    <footer className="relative bg-graphite-950 border-t border-white/5">
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary-900/5 to-transparent pointer-events-none" />

      {/* Stats Section */}
      <div className="border-b border-white/5">
        <div className="container-premium py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 mx-auto mb-3 text-primary-400" />
                <div className="stats-number">{stat.value}</div>
                <div className="text-graphite-400 text-sm mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-premium py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg blur-lg opacity-50" />
                <div className="relative w-14 h-14 bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-display font-bold text-xl">GSM</span>
                </div>
              </div>
              <div>
                <h2 className="text-white font-display font-bold text-xl">GSM CORPORATION</h2>
                <p className="text-xs text-graphite-500 tracking-widest uppercase">Transforming India</p>
              </div>
            </Link>
            <p className="text-graphite-400 mb-6 leading-relaxed">
              A dynamic and rapidly growing organization specializing in comprehensive solutions
              for government and institutional projects since 2017.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-primary-400" />
                <div className="flex flex-col">
                  <a href="tel:+919923100592" className="text-graphite-300 hover:text-white transition-colors">
                    +91 99231 00592
                  </a>
                  <a href="tel:+919960854675" className="text-graphite-400 text-sm hover:text-white transition-colors">
                    +91 99608 54675
                  </a>
                </div>
              </div>
              <a href="mailto:gsmcorpindia@gmail.com" className="flex items-center gap-2 text-graphite-400 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-primary-400" />
                <span>gsmcorpindia@gmail.com</span>
              </a>
              <div className="flex items-start gap-2 text-graphite-400">
                <MapPin className="w-4 h-4 text-primary-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span>FC Road, Pune, Maharashtra, India</span>
                  <p className="text-sm text-graphite-500">Operational Area: Across Maharashtra</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3 mt-6">
              {[Linkedin, Twitter, Facebook, Youtube].map((Icon, index) => (
                <a
                  key={index}
                  href="#"
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-graphite-400 hover:text-white hover:bg-primary-500 hover:border-primary-500 transition-all"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h3 className="text-white font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-graphite-400 hover:text-white transition-colors flex items-center gap-1 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-graphite-400 hover:text-white transition-colors flex items-center gap-1 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">Industries</h3>
            <ul className="space-y-2">
              {footerLinks.industries.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-graphite-400 hover:text-white transition-colors flex items-center gap-1 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">Partnerships</h3>
            <ul className="space-y-2">
              {footerLinks.partnerships.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-graphite-400 hover:text-white transition-colors flex items-center gap-1 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Group Companies */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <h3 className="text-white font-semibold mb-4">Group Companies (11)</h3>
          <div className="flex flex-wrap gap-2">
            {groupCompanies.map((company, index) => (
              <span
                key={index}
                className="px-3 py-1.5 text-sm text-graphite-400 bg-white/5 rounded-lg border border-white/10 hover:border-primary-500/50 hover:text-white transition-all cursor-pointer"
              >
                {company}
              </span>
            ))}
          </div>
        </div>

        {/* Partners */}
        <div className="mt-8 pt-8 border-t border-white/5">
          <h3 className="text-white font-semibold mb-4">Our Partners & Brands</h3>
          <div className="flex flex-wrap gap-2">
            {partners.map((partner, index) => (
              <span
                key={index}
                className="px-3 py-1.5 text-sm text-graphite-400 bg-white/5 rounded-lg border border-white/10 hover:border-accent-500/50 hover:text-white transition-all cursor-pointer"
              >
                {partner}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="container-premium py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-graphite-500 text-sm">
              © {new Date().getFullYear()} GSM Corporation. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-graphite-500 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-graphite-500 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/sitemap" className="text-graphite-500 hover:text-white text-sm transition-colors">
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}